import {
  createInstance,
  getDeviceId,
  Identify,
  identify,
  Types
} from '@amplitude/analytics-browser'
import clevertap from 'clevertap-web-sdk'
import i18n from 'i18n'
import Logger from 'services/Logger'
import { SingularConfig, singularSdk } from 'singular-sdk'
import { AccountDoc } from 'types/api/changes'
import { highestActiveProduct } from 'utils/api/accounts'
import { getCookie } from 'utils/cookies'
import { isInIframe, isTestEnv } from 'utils/env'

import {
  AnalyticsSurface,
  getAnalyticsSentinelId,
  getAnalyticsSurface,
  isInAppEmbedPreview,
  isPublicAnalyticsSurface
} from './analyticsSurface'
import baseEventTracker, { ConfigType, EventsProps } from './baseEventTracker'

export interface AnalyticsProperty {
  key: UserProperties
  value: any
}

export interface DeviceProps {
  amplitudeDeviceId?: any
  singularDeviceId?: any
  clevertapDeviceId?: any
}

export default class Analytics implements baseEventTracker {
  private static instance: Analytics
  private static lastInitKey?: string
  // Store the amplitude instance
  private amplitudeInstance: Types.BrowserClient | undefined

  private static normalizeDeviceId(id?: unknown): string | undefined {
    if (id == null) return undefined

    const value = String(id)
    if (!value || value === 'undefined' || value === 'null') {
      return undefined
    }

    return value
  }

  private static buildInitKey(
    surface: AnalyticsSurface,
    amplitudeDeviceId?: string,
    singularDeviceId?: string,
    clevertapDeviceId?: string
  ): string {
    return [
      surface,
      amplitudeDeviceId ?? '',
      singularDeviceId ?? '',
      clevertapDeviceId ?? ''
    ].join('|')
  }

  public static getInstance(deviceProps?: DeviceProps): Analytics {
    if (isInAppEmbedPreview()) {
      if (this.instance && this.lastInitKey === 'inAppPreview') {
        return this.instance
      }

      const instance = new Analytics()
      this.instance = instance
      this.lastInitKey = 'inAppPreview'
      return instance
    }

    const surface = getAnalyticsSurface()
    const isPublicSurface = isPublicAnalyticsSurface(surface)
    const sentinelId = getAnalyticsSentinelId(surface)
    const amplitudeDeviceId = isPublicSurface
      ? sentinelId
      : this.normalizeDeviceId(
          getCookie('amplitudeId') || deviceProps?.amplitudeDeviceId
        )
    const singularDeviceId = isPublicSurface
      ? sentinelId
      : this.normalizeDeviceId(
          getCookie('singularId') || deviceProps?.singularDeviceId
        )
    const clevertapDeviceId = isPublicSurface
      ? sentinelId
      : this.normalizeDeviceId(
          getCookie('clevertapId') || deviceProps?.clevertapDeviceId
        )
    const initKey = this.buildInitKey(
      surface,
      amplitudeDeviceId,
      singularDeviceId,
      clevertapDeviceId
    )

    if (this.instance && this.lastInitKey === initKey) {
      return this.instance
    }

    const instance = new Analytics()
    this.instance = instance
    this.lastInitKey = initKey

    // Amplitude configuration
    let amplitudeConfig: Types.BrowserOptions = {
      logLevel: 0,
      // Page views are Amplitude autocapture: they use document.title, have no
      // surface, and fire repeatedly in this SPA. Sessions stay on for the full
      // app only (real app opens).
      defaultTracking: {
        pageViews: false,
        sessions: !isPublicSurface,
        fileDownloads: false,
        formInteractions: false,
        attribution: false
      }
    }

    if (isPublicSurface) {
      amplitudeConfig = {
        ...amplitudeConfig,
        deviceId: sentinelId
      }
    } else if (amplitudeDeviceId) {
      amplitudeConfig = {
        ...amplitudeConfig,
        deviceId: amplitudeDeviceId
      }
      Logger.debug('custom device id for amplitude', amplitudeDeviceId)
    }

    // Initialize Amplitude with v2 approach
    instance.amplitudeInstance = createInstance()
    instance.amplitudeInstance.init(
      process.env.REACT_APP_AMPLITUDE_KEY ?? '',
      isPublicSurface ? sentinelId : undefined,
      amplitudeConfig
    )

    // Initialize Singular
    let config = new SingularConfig(
      process.env.REACT_APP_SINGULAR_KEY,
      process.env.REACT_APP_SINGULAR_SECRET,
      isTestEnv() ? 'app.groupcal.stage002-app' : 'app.groupcal.app'
    )

    if (singularDeviceId) {
      config = config.withPersistentSingularDeviceId(singularDeviceId)
      Logger.debug('custom device id for singular', singularDeviceId)
    }

    singularSdk.init(config) // singular init

    clevertap.init(isTestEnv() ? 'TEST-R59-58R-655Z' : 'R7R-47Z-K55Z') // clevertap init
    clevertap.spa = true

    if (isPublicSurface && sentinelId) {
      instance.setPublicSurfaceIdentity(sentinelId)
    }

    return instance
  }

  sendEvent(eventName: Events, eventProps: EventsProps = {}) {
    if (isInAppEmbedPreview()) {
      return
    }

    if (eventProps) {
      const surface = getAnalyticsSurface()
      const props: EventsProps = {
        ...eventProps,
        [EventProperties.SURFACE]:
          eventProps[EventProperties.SURFACE] ?? surface
      }

      if (isPublicAnalyticsSurface(surface)) {
        props[EventProperties.IS_IFRAME] = isInIframe() ? 'Yes' : 'No'
      }

      Logger.pink('analytics', props, eventName)

      // Updated Amplitude event tracking format
      this.amplitudeInstance?.track({
        event_type: eventName,
        event_properties: props
      })

      // Other analytics providers remain the same
      singularSdk.event(eventName, props)
      clevertap.event.push(eventName, props)
    }
  }

  sendOutOfTierDialogAnalytics(
    eventName: Events,
    isOwner: 'Yes' | 'No',
    subscription?: string,
    features?: string[],
    neededTier?: string,
    flow?: string
  ) {
    const props: EventsProps = {}

    props.category = 'Group_Blocked'
    props['Is owner'] = isOwner
    props.Flow = flow

    if (isOwner === 'Yes') {
      props['Is active subscription'] = subscription ? 'Yes' : 'No'
      props['Active subscription'] = subscription
      props['Features opened with'] = features
      props['Needed tier'] = neededTier
    }

    this.sendEvent(eventName, props)
  }

  setAnalyticsUserId(userId: string) {
    if (isInAppEmbedPreview() || isPublicAnalyticsSurface()) {
      return
    }

    this.applyUserId(userId)
  }

  private setPublicSurfaceIdentity(sentinelId: string) {
    this.applyUserId(sentinelId)
  }

  private applyUserId(userId: string) {
    singularSdk.login(userId)

    // Updated Amplitude user ID setting
    this.amplitudeInstance?.setUserId(userId)

    clevertap.profile.push({
      Site: {
        ['Identity']: userId
      }
    })
  }

  setUserProperties(properties: AnalyticsProperty) {
    if (isInAppEmbedPreview() || isPublicAnalyticsSurface()) {
      return
    }

    Logger.orange('Analytics:SettingUserProperties', properties)
    clevertap.profile.push({
      Site: { [properties.key]: properties.value }
    })

    const identifyObj = new Identify()
    identifyObj.set(properties.key, properties.value)
    this.amplitudeInstance?.identify(identifyObj)
  }

  get singularDeviceId() {
    return singularSdk.getSingularDeviceId()
  }

  get amplitudeDeviceId() {
    return getDeviceId()
  }

  removeUserProperty(key: string) {
    const identifyObj = new Identify()
    identifyObj.unset(key)
    identify(identifyObj)
  }

  appendSubscriptionInfo(account: AccountDoc) {
    if (isInAppEmbedPreview() || isPublicAnalyticsSurface()) {
      return
    }

    const product = highestActiveProduct(account)

    this.setUserProperties({
      key: UserProperties.IS_ACTIVE_SUBS,
      value: product != null ? 'Yes' : 'No'
    })

    if (product) {
      this.setUserProperties({
        key: UserProperties.ACTIVE_SUBS,
        value: product?.ID
      })
    }
  }

  handleLogout() {
    if (isInAppEmbedPreview() || isPublicAnalyticsSurface()) {
      return
    }

    singularSdk.logout()
    // You may also want to reset the Amplitude user ID
    this.amplitudeInstance?.setUserId(undefined)
  }

  updateLang() {
    this.setUserProperties({
      key: UserProperties.LANG_CODE,
      value: getLanguageCode()
    })
  }
}

export enum UserProperties {
  APP_VERSION = 'App version',
  LANG_CODE = 'LanguageCode',
  IS_ACTIVE_SUBS = 'Is active subscription', // “Yes”/“No”
  ACTIVE_SUBS = 'Active subscription',
  INVITED = 'InvitedByAnotherUser', // (send only in case yes)
  IS_IN_ACTIVE_TRIAL = 'Is in active trial',
  IS_TRIAL_EXPIRED = 'Is trial expired'
}

export enum Events {
  FIRST_SCREEN = 'PAGE_LOAD_Tutorial_Screen',
  LOGIN_TUTOR = 'Click_Login_Tutorial_Screen',
  LOGIN_ERROR = 'Login_Error',
  LOGIN_SUCCESS = 'Login_Success',
  CREATE_ACC_SUCCESS = 'Create_Account_Success',
  CREATE_ACC_FAILURE = 'SignUp_Error',
  FIRST_MAIN_SCREEN = 'User_Saw_Main_Screen_For_first_Time',
  ADD_GROUP = 'Click_Add_New_Group',
  ADDED_GROUP = 'New_Group_Added',
  EVENT_START_ADD_EVENT = 'Click_Add_New_Item',
  ITEM_ADDED = 'Item_Added',
  LOGOUT = 'Click_Settings_Logout',
  PAGE_LOAD_ENTER_PHONE = 'PAGE_LOAD_Enter_phone_number',
  PHONE_VERIF_REQUEST_SENT = 'Phone_verification_request_sent',
  PHONE_VERIF_REQ_ERROR_FIREBASE = 'Phone_verification_request_Firebase_Verification_Error',
  PHONE_VERIF_REQ_DONE = 'Phone_verification_request_done',

  PAGE_LOAD_VERIF = 'PAGE_LOAD_Verification',
  PHONE_VERIF_INCORRECT = 'Phone_verification_incorrect',
  PHONE_VERIF_CORRECT = 'Phone_verification_correct',
  OPEN_INAPP_PURCHASE = 'Open_In_App_Purchase_Popup',
  CLICK_INAPP_PURCHASE = 'Click_In_App_Purchase_Buy',
  INAPP_PURCHASED = 'In_App_Purchase_Purchased',
  ITEM_DELETED = 'Delete_Item',
  blockedGroupDialogCancelClick = 'Blocked_group_dialog_cancel_clicked',
  blockedGroupDialogDowngradeClick = 'Blocked_group_dialog_downgrade_clicked',
  blockedGroupDialogUpgradeClick = 'Blocked_group_dialog_upgrade_clicked',
  blockedDialogPresented = 'Blocked_group_dialog_prenseted',
  GEUST_MODE_OPEN_FULL_VERSION = 'Click_guest_mode_open_full_version',
  GEUST_MODE_OPEN = 'Guest_mode_open',
  EMBED_MODE_OPEN = 'Embed_mode_open',
  INAPP_CANCELLED = 'In_App_Purchase_Canceled'
}

export enum EventProperties {
  GROUP_NAME = 'Group Name',
  NUM_OF_PARTICIPANTS = 'Num Of Participants',
  ALLOW_META_DATA = 'Allow MetaData Changes',
  ALLOW_ADD_EDIT_EVENT = 'Allow Add Edit Items',
  ALLOW_ADD_PARTICIPANTS = 'Allow Adding Participants',
  ALLOW_EDIT_THEIR_EVENT = 'Allow Edit Their Event Only',
  METHOD_OF_SHARING = 'Method of sharing',
  METHOD_CONTACT_LIST = 'Contact list',
  METHOD_LINK = 'Link',
  LOCATION_ADD_EVENT = 'Location tapped',
  EVENT_FROM_SCREEN = 'Plus icon',
  ADD_EVENT_FROM_LONG_TAP = 'Long tap on calendar',
  ITEM_TYPE = 'Type',
  CATEGORY = 'category',
  SELECTED_PHONE_TYPE = 'Selected_phone_type',
  COUNTRY_CODE = 'Country_code',
  PHONE_NUMBER = 'Phone_number',
  ERROR = 'error',
  FEATURES_OPENED_WITH = 'Features opened with',
  NEEDED_TIER = 'Needed tier',
  IS_OWNER = 'Is owner',
  FLOW = 'Flow',
  IS_PURCHASING_TRIAL = 'Is purchasing trial',
  TRIAL_OR_PLAN_EXPIRES_IN = 'Trial or plan expires in',
  SURFACE = 'surface',
  IS_IFRAME = 'is_iframe'
}

export enum EventCategory {
  PHONE_NUMBER = 'Enter_phone_number',
  CODE_VERIF = 'Code_verification'
}

export enum BlockedFlow {
  groupInfo = 'Group Info',
  addItem = 'Add item',
  editItem = 'Edit item'
}

/**
 * Gets the appropriate language code for analytics
 * - Returns full locale (e.g. "en-us") if it's American English
 * - Returns language code only (e.g. "fr") for other languages
 */
export function getLanguageCode(): string {
  // Get current language from i18n
  const fullLocale = i18n.language.toLowerCase() // e.g. "en-us", "fr-fr"

  // Get just the language part (e.g. "en", "fr")
  const langCode = fullLocale.split('-')[0].toLowerCase()

  // Special case for American English
  if (fullLocale === 'en-us') {
    return fullLocale
  }

  return langCode
}
